import { useItemTransfers } from "../../hooks/useTransfers";
import {
  Card,
  CardContent,
  Skeleton,
  Badge
} from '@tonic/shared/components/ui';
import { formatDistanceToNow } from "date-fns";
import { ArrowRight } from "lucide-react";
import PropTypes from "prop-types";

/**
 * @typedef {Object} Location
 * @property {string} name - Location name
 * @property {string} code - Location code
 */

/**
 * @typedef {Object} TransferItem
 * @property {string} id - Item ID
 * @property {number} quantity - Transfer quantity
 */

/**
 * @typedef {Object} Transfer
 * @property {string} id - Transfer ID
 * @property {string} reference - Transfer reference number
 * @property {string} status - Transfer status
 * @property {Date} createdAt - Creation date
 * @property {Location} sourceLocation - Source location
 * @property {Location} destinationLocation - Destination location
 * @property {TransferItem[]} items - Transferred items
 */

/**
 * @typedef {Object} ItemTransfersProps
 * @property {string} itemId - ID of the item to show transfers for
 */

/**
 * Gets the appropriate color class for a transfer status
 * @param {string} status - The transfer status
 * @returns {string} The color class for the badge
 */
function getStatusColor(status) {
  const colors = {
    pending: "bg-yellow-500",
    in_transit: "bg-blue-500",
    completed: "bg-green-500",
    cancelled: "bg-red-500",
  };
  return colors[status] || "bg-gray-500";
}

/**
 * Component that displays a list of transfers for an inventory item
 * @param {ItemTransfersProps} props
 * @returns {JSX.Element} The item transfers component
 */
export function ItemTransfers({ itemId }) {
  const { data: transfers, isLoading } = useItemTransfers(itemId);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-[150px]" />
                  <Skeleton className="h-4 w-[250px]" />
                </div>
                <Skeleton className="h-4 w-[100px]" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!transfers?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <p className="text-muted-foreground">No transfers found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {transfers.map((transfer) => (
        <Card key={transfer.id}>
          <CardContent className="p-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Badge variant="outline">{transfer.reference}</Badge>
                  <Badge className={getStatusColor(transfer.status)}>
                    {transfer.status.replace("_", " ")}
                  </Badge>
                </div>
                <div className="text-sm text-muted-foreground">
                  {formatDistanceToNow(new Date(transfer.createdAt), {
                    addSuffix: true,
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div>
                    <div className="font-medium">
                      {transfer.sourceLocation.name}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {transfer.sourceLocation.code}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="font-medium">
                      {transfer.destinationLocation.name}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {transfer.destinationLocation.code}
                    </div>
                  </div>
                </div>
                <Badge variant="outline" className="font-mono">
                  Qty: {transfer.items.find((item) => item.id === itemId)?.quantity || 0}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

ItemTransfers.propTypes = {
  itemId: PropTypes.string.isRequired,
}; 
